<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="light">
                <ion-buttons slot="start">
                    <ion-button color="primary" @click="cancel">
                        <ion-icon slot="icon-only" :icon="close"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title>{{ label }}</ion-title>
                <ion-buttons slot="end">
                    <!-- Grammar correction button, only for admin (user id 1) -->
                    <ion-button v-if="$store.getters.getUser.id == 1" color="primary" @click="correctGrammar" :disabled="!canUseGrammarCheck">
                        <mdicon name="spellcheck"></mdicon>
                    </ion-button>

                    <!-- Grammar suggestion popover -->
                    <ion-popover size="auto" :arrow="true" :is-open="popoverOpen" :trigger="popoverTriggerId" @didDismiss="popoverOpen = false">
                        <ion-header class="ion-no-border">
                            <ion-toolbar color="white">
                                <ion-buttons slot="end">
                                    <ion-button color="primary" @click="popoverOpen = false">
                                        <mdicon name="close"></mdicon>
                                    </ion-button>
                                </ion-buttons>
                            </ion-toolbar>
                        </ion-header>
                        <ion-content>
                            <div class="px-5">
                                <div class="flex align-center justify-center mb-3" v-if="loading_suggestion">
                                    <ion-spinner name="crescent"></ion-spinner>
                                    <small class="ml-2">Laden</small>
                                </div>

                                <div v-else>
                                    <div class="mb-5">
                                        <small>
                                            {{ suggestion }}
                                        </small>
                                    </div>
                                    <ion-footer class="flex space-between align-center">
                                        <!-- Retry button -->
                                        <ion-button expand="block" fill="clear" color="medium" @click="correctGrammar">
                                            <mdicon name="refresh"></mdicon>
                                        </ion-button>

                                        <!-- Accept suggestion button -->
                                        <ion-button expand="block" fill="clear" color="primary" @click="replaceSuggestion">
                                            <mdicon name="check-circle"></mdicon>
                                        </ion-button>
                                    </ion-footer>
                                </div>
                            </div>
                        </ion-content>
                    </ion-popover>

                    <!-- Save button -->
                    <ion-button color="primary" @click="confirm" :disabled="!canSave">
                        <mdicon name="content-save"></mdicon>
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-header>
        <ion-content color="light">
            <!-- Main text input fields -->
            <ion-list :inset="true">
                <ion-item lines="none">
                    <ion-textarea rows="3" mode="ios" ref="remark_field" @ionChange="handleTextChange" :spellcheck="true" autofocus autocorrect="on" autocapitalize="sentences" :minlength="10" placeholder="Schrijf zelf een constatering of kies uit één van de standaard constateringen hieronder." :autocomplete="true" :autoGrow="true" v-model="editable_value">
                    </ion-textarea>
                </ion-item>
            </ion-list>

            <ion-list :inset="true" class="mt-3">
                <ion-item lines="none">
                    <ion-textarea rows="5" mode="ios" ref="remark_description" :spellcheck="true" autocorrect="on" autocapitalize="sentences" :minlength="10" placeholder="Optionele toelichting" :autocomplete="true" :autoGrow="true" v-model="editable_description">
                    </ion-textarea>
                </ion-item>
            </ion-list>

            <!-- Refresh control -->
            <ion-refresher ref="refresher" slot="fixed" pull-factor="0.5" pull-min="100" pull-max="200" @ionRefresh="loadRemarks">
                <ion-refresher-content></ion-refresher-content>
            </ion-refresher>

            <!-- Search bar and remark list -->
            <ion-item class="px-0" color="light" lines="none">
                <SearchBar position="start" mode="ios" v-model="search" placeholder="Zoek in standaard toelichtingen" ref="searchbar">
                </SearchBar>
            </ion-item>

            <!-- Remarks count display -->
            <ion-item v-if="remarksCountText" color="light" lines="none" class="px-3 pt-3">
                <ion-note slot="start" color="medium">
                    <small>{{ remarksCountText }}</small>
                </ion-note>
            </ion-item>

            <ion-list :inset="true">
                <ion-item-sliding class="child-item" v-for="item in filteredRemarks" :key="item.id">
                    <ion-item button lines="inset" class="ripple-parent" @click="selectRemark(item)">
                        <ion-label>
                            {{ item.name }}
                        </ion-label>
                    </ion-item>
                    <ion-item-options>
                        <ion-item-option :disabled="!item.private" color="danger" @click="deleteCustomRemark(item.id)">
                            Verwijderen
                        </ion-item-option>
                    </ion-item-options>
                </ion-item-sliding>
                <ion-ripple-effect></ion-ripple-effect>
            </ion-list>
        </ion-content>
    </ion-page>
</template>

<script>
import { modalController } from "@ionic/vue";
import { close, save } from "ionicons/icons";
import { IonPopover } from "@ionic/vue";
import { db } from "@/services/db";
import { v4 as uuidv4 } from "uuid";
import {
    alertController,
    IonSearchbar,
    IonItemSliding,
    IonItemOptions,
    IonItemOption,
    IonRippleEffect,
} from "@ionic/vue";
import Fuse from "fuse.js";

// Minimum text length requirements
const MIN_TEXT_LENGTH = 10;
// Maximum results to show in search
const MAX_SEARCH_RESULTS = 10;

export default {
    name: "TextareaEditor",
    props: {
        value: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: "Editor"
        },
        placeholder: {
            type: String,
            default: ""
        },
        description: {
            type: String,
            default: ""
        }
    },
    components: {
        IonSearchbar,
        IonItemSliding,
        IonItemOptions,
        IonItemOption,
        IonRippleEffect,
        IonPopover,
    },
    data() {
        return {
            // UI controls
            popoverOpen: false,
            popoverTriggerId: "grammar-popover",
            close,
            save,

            // Text values
            editable_value: "",
            editable_description: "",

            // Remarks data
            remarks: [],
            cachedRemarks: null,
            selected_remark_id: null,

            // UI state
            search: "",
            suggestion: null,
            loading_suggestion: false,
            shouldSaveAsCustom: false,
            isModified: false,
        };
    },
    created() {
        this.editable_value = this.value;
        this.editable_description = this.description;
        this.loadRemarks();
    },
    methods: {

        /**
         * Replace current text with the suggested improvement
         */
        replaceSuggestion() {
            if (this.suggestion) {
                this.editable_value = this.suggestion;
                this.handleTextChange();
                this.popoverOpen = false;
            }
        },

        /**
         * Request grammar correction from API
         */
        async correctGrammar() {
            if (!this.canUseGrammarCheck) return;

            this.popoverOpen = true;
            this.loading_suggestion = true;

            try {
                const response = await this.$axios.post(
                    localStorage.getItem("api_url") + "/openai",
                    {
                        what_to_do: "Herschrijf deze zin. Het wordt gebruikt in elektrotechnische inspectie rapportage: ",
                        user_input: this.editable_value,
                    }
                );
                this.suggestion = response.data.message;
            } catch (error) {
                console.error("Grammar correction failed:", error);
                this.suggestion = "Er kon geen suggestie worden gegenereerd. Probeer het later opnieuw.";
            } finally {
                this.loading_suggestion = false;
            }
        },

        /**
         * Handle changes to the main text input
         */
        handleTextChange() {
            // If text is empty or too short, reset state
            if (!this.editable_value || this.editable_value.length < MIN_TEXT_LENGTH) {
                this.shouldSaveAsCustom = false;
                this.isModified = false;
                return;
            }

            // Check if text differs from selected remark
            if (this.selected_remark_id) {
                const selectedRemark = this.remarks.find(r => r.id === this.selected_remark_id);
                if (selectedRemark && this.editable_value !== selectedRemark.name) {
                    this.isModified = true;
                    this.shouldSaveAsCustom = true;
                    this.selected_remark_id = null;
                }
            } else {
                // New custom text that's long enough
                this.shouldSaveAsCustom = true;
                this.isModified = true;
            }
        },

        /**
         * Delete a custom remark
         */
        deleteCustomRemark(id) {
            if (!id) return;

            this.$requests.delete("remarks", "/remarks/" + id, id)
                .then(() => {
                    this.remarks = this.remarks.filter(remark => remark.id !== id);
                    this.$toast.success("De toelichting is verwijderd");

                    // If the deleted remark was selected, clear the selection
                    if (this.selected_remark_id === id) {
                        this.selected_remark_id = null;
                    }

                    // Update cache
                    this.updateCache();
                })
                .catch(error => {
                    console.error("Failed to delete remark:", error);
                    this.$toast.error("Kon de toelichting niet verwijderen");
                });
        },

        /**
         * Save the current text as a custom remark
         */
        saveAsCustomRemark() {
            if (!this.canSave) return;

            const newRemark = {
                id: uuidv4(),
                name: this.editable_value,
                description: this.editable_description || "",
                private: true,
                user_id: this.$store.getters.getUser.id,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
            };

            db.remarks.add(newRemark)
                .then(() => {
                    this.$toast.success("De toelichting is opgeslagen");
                    this.remarks.push(newRemark);
                    this.selected_remark_id = newRemark.id;
                    this.shouldSaveAsCustom = false;
                    this.isModified = false;

                    // Sort and update cache
                    this.sortRemarks();
                    this.updateCache();
                })
                .catch(error => {
                    console.error("Failed to save custom remark:", error);
                    this.$toast.error("Kon de toelichting niet opslaan");
                });
        },

        /**
         * Select a remark from the list
         */
        selectRemark(remark) {
            if (!remark) return;

            this.editable_value = remark.name;
            this.editable_description = remark.description || "";
            this.selected_remark_id = remark.id;
            this.shouldSaveAsCustom = false;
            this.isModified = false;

            // Focus the text field for further editing
            this.$nextTick(() => {
                this.$refs.remark_field.$el.setFocus();
            });
        },

        /**
         * Load remarks from database
         */
        async loadRemarks($event = null) {
            try {
                // Check if we have cached remarks
                if (this.cachedRemarks) {
                    this.remarks = [...this.cachedRemarks];
                } else {
                    // Get remarks from database
                    const allRemarks = await db.remarks.toArray();

                    // Filter for current user
                    this.remarks = allRemarks.filter(
                        remark => remark.user_id == null || remark.user_id == this.$store.getters.getUser.id
                    );

                    // Sort the remarks
                    this.sortRemarks();

                    // Cache the results
                    this.updateCache();
                }
            } catch (error) {
                console.error("Failed to load remarks:", error);
                this.$toast.error("Kon de toelichtingen niet laden");
                this.remarks = [];
            } finally {
                // Complete the refresh if triggered by pull-to-refresh
                if ($event) {
                    $event.target.complete();
                }
            }
        },

        /**
         * Sort remarks alphabetically
         */
        sortRemarks() {
            this.remarks.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        },

        /**
         * Update the remarks cache
         */
        updateCache() {
            this.cachedRemarks = [...this.remarks];
        },

        /**
         * Cancel and close the modal
         */
        cancel() {
            modalController.dismiss(null, "cancel");
        },

        /**
         * Confirm and save changes
         */
        async confirm() {
            let userWantsToSave = false;

            // If should save as custom remark, ask user first
            if (this.shouldSaveAsCustom && this.isModified) {
                const alert = await alertController.create({
                    mode: "ios",
                    header: "Toelichting opslaan",
                    message: "Wil je deze toelichting opslaan als standaard toelichting?",
                    buttons: [
                        {
                            text: "Nee",
                            role: "cancel",
                            cssClass: "secondary",
                            handler: () => {
                                userWantsToSave = false;
                            },
                        },
                        {
                            text: "Ja",
                            role: "save",
                            handler: () => {
                                userWantsToSave = true;
                                this.saveAsCustomRemark();
                            },
                        },
                    ],
                });

                await alert.present();
                await alert.onDidDismiss();
            }

            // Return data and close modal
            modalController.dismiss(
                {
                    value: this.editable_value,
                    description: this.editable_description,
                    save_private_remark: userWantsToSave,
                    auto_classify: await this.updateIB22(),
                },
                "confirm"
            );
        },
        async updateIB22() {
            // if the inspection method is IB22
            if (this.$store.getters.getInspectionMethodCode != 'IB22') {
                return
            }
            // we search db.remarks.name for the remark with observation.remark
            let remark = await db.remarks.where({ name: this.editable_value }).first();

            if (remark) {
                // Only return classification if the remark has classification data
                if (remark.danger_aspect_id || remark.error_indication_id ||
                    remark.error_classification_id || remark.repair_period_id) {
                    let result = {
                        danger_aspect_id: remark.danger_aspect_id,
                        error_indication_id: remark.error_indication_id,
                        error_classification_id: remark.error_classification_id,
                        repair_period_id: remark.repair_period_id,
                    }
                    return result
                }
            }

            // Return undefined to indicate no classification update should occur
            // This preserves existing classification when selecting remarks without classification
            return undefined
        },
    },
    computed: {
        /**
         * Check if the save button should be enabled
         */
        canSave() {
            return this.editable_value && this.editable_value.length >= MIN_TEXT_LENGTH;
        },

        /**
         * Check if grammar checking can be used
         */
        canUseGrammarCheck() {
            return this.editable_value &&
                this.editable_value.length >= MIN_TEXT_LENGTH &&
                this.$store.getters.online;
        },

        /**
         * Get filtered remarks based on search
         */
        filteredRemarks() {
            if (!this.remarks || this.remarks.length === 0) {
                return [];
            }

            // If no search, return first batch
            if (!this.search) {
                return this.remarks.slice(0, MAX_SEARCH_RESULTS);
            }

            // Search configuration
            const options = {
                // Search in both name and description fields
                keys: [
                    { name: "name", weight: 1.5 },    // Give name higher priority
                    { name: "description", weight: 1 }
                ],
                // Stricter threshold for more precise matching
                threshold: 0.4,
                // Improved handling of special characters
                useExtendedSearch: true,
                // Sort results by relevance
                shouldSort: true,
                // Include matches information for highlighting
                includeMatches: true
            };

            const fuse = new Fuse(this.remarks, options);
            const results = fuse.search(this.search);

            // Return exact number of results for more precise matching
            return results
                .map(result => result.item)
                .slice(0, MAX_SEARCH_RESULTS);
        },

        /**
         * Check if running on Android
         */
        isAndroid() {
            return this.$platform === 'android';
        },
        /**
         * Get display text for remarks count
         */
        remarksCountText() {
            const totalRemarks = this.remarks.length;
            const displayedRemarks = this.filteredRemarks.length;

            if (!totalRemarks) {
                return '';
            }

            if (this.search) {
                // When searching, show number of results found
                return `${displayedRemarks} ${displayedRemarks === 1 ? 'resultaat' : 'resultaten'} gevonden`;
            } else {
                // When not searching, show X of Y remarks
                const showing = Math.min(MAX_SEARCH_RESULTS, totalRemarks);
                return `Toont ${showing} van ${totalRemarks} constateringen`;
            }
        }
    }
};
</script>

<style lang="scss">
.ripple-parent {
    position: relative;
    overflow: hidden;
}
</style>
